// The chrome both roles share: a collapsible desktop sidebar, a mobile drawer,
// a sticky top bar, an optional contextual tab row, and the animated content
// area.
//
// Extracted from GardenShell when teachers moved off the top-bar AppShell. The
// alternative — a second sidebar implementation — would have meant two copies
// of the collapse persistence, the drawer, the focus handling and the route
// transition, drifting apart the first time one was touched. Everything
// role-specific (nav items, tabs, header extras) arrives as props.
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Icon, type IconName } from '@/components/garden';
import { ProfileMenu } from '@/components/ProfileMenu';
import { RoleBadge } from '@/components/RoleBadge';
import { ThemeToggle } from '@/components/ThemeToggle';
import { fontPx } from '@/lib/fontSize';

export interface ShellNavItem {
  to: string;
  label: string;
  icon: IconName;
}

export interface ShellTab {
  to: string;
  label: string;
}

const sidebarLinkStyle = (isActive: boolean, collapsed = false): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'flex-start',
  gap: '11px',
  padding: collapsed ? '10px' : '10px 13px',
  borderRadius: '12px',
  fontSize: fontPx(14),
  fontWeight: isActive ? 700 : 600,
  textDecoration: 'none',
  color: isActive ? 'var(--slab-strong)' : 'var(--sidebar-link)',
  background: isActive ? 'var(--brand-lime)' : 'transparent',
  transition:
    'background var(--dur-fast) var(--ease-standard), color var(--dur-fast) var(--ease-standard)',
});

/**
 * Should this entry match its path EXACTLY rather than its whole subtree?
 *
 * A NavLink lights up for its path and everything under it, which is what keeps
 * "Practice" lit on /practice/cards — right for a section parent. It is wrong for
 * an entry that another entry lives beneath: that one prefixes its siblings and
 * stays lit next to whichever is actually open. `/admin` did exactly this, so
 * "Schools" was highlighted alongside "Staff" the whole time.
 *
 * Derived from the nav itself rather than configured per shell. The hardcoded
 * `item.to === '/teacher'` this replaced covered one console and silently stopped
 * applying the moment that path stopped being a nav entry.
 */
function matchesExactly(item: ShellNavItem, items: ShellNavItem[]): boolean {
  return items.some((other) => other.to !== item.to && other.to.startsWith(`${item.to}/`));
}

function PrimaryNav({ items, collapsed = false }: { items: ShellNavItem[]; collapsed?: boolean }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={matchesExactly(item, items)}
          aria-label={collapsed ? item.label : undefined}
          title={collapsed ? item.label : undefined}
          style={({ isActive }) => sidebarLinkStyle(isActive, collapsed)}
        >
          <Icon name={item.icon} size={17} />
          <span className={collapsed ? 'sr-only' : undefined}>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SectionTabs({ tabs, label }: { tabs: ShellTab[] | undefined; label: string }) {
  if (!tabs || tabs.length === 0) return null;
  return (
    <nav aria-label={label} className="overflow-x-auto border-b border-line bg-card">
      <div className="mx-auto flex w-full gap-1 px-5" style={{ maxWidth: '1600px' }}>
        {tabs.map((tab, index) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={index === 0}
            className={({ isActive }) =>
              `relative shrink-0 px-3 py-3 text-sm font-semibold no-underline ${
                isActive ? 'text-forest' : 'text-ink-soft hover:text-forest'
              }`
            }
            style={{ transition: 'color var(--dur-fast) var(--ease-standard)' }}
          >
            {({ isActive }) => (
              <>
                {tab.label}
                {/* A shared layoutId slides the underline between tabs instead
                    of snapping it, which is what makes the row read as one
                    control rather than four independent links. */}
                {isActive && (
                  <motion.span
                    layoutId="section-tab-underline"
                    className="absolute inset-x-0 bottom-0 h-[2px] bg-forest"
                    transition={{ type: 'tween', duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                  />
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export interface SidebarShellProps {
  nav: ShellNavItem[];
  /** Contextual tabs for the section the current path falls in, if any. */
  tabs?: ShellTab[];
  /** Where the logo links to. */
  homeTo: string;
  /**
   * A line in the top bar that says something the badge beside it does not —
   * the student's cohort. Staff pass nothing: their subtitle WAS their role, so
   * the header stated it twice, once as text and once as the RoleBadge.
   */
  subtitle?: string;
  /** Pinned above the profile menu (students get "Ask a doubt"). */
  footerNav?: ShellNavItem;
  /** Streak pill, notification bell — anything role-specific in the top bar. */
  headerExtras?: ReactNode;
  /** Persistence key, so a teacher's collapse preference is their own. */
  collapseKey: string;
}

export function SidebarShell({
  nav,
  tabs,
  homeTo,
  subtitle,
  footerNav,
  headerExtras,
  collapseKey,
}: SidebarShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.localStorage.getItem(collapseKey) === 'true',
  );
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const hasTabs = Boolean(tabs && tabs.length > 0);

  // Where am I — by longest matching prefix, so /practice/cards resolves to
  // Practice rather than to whichever nav item happens to be listed first.
  const sectionLabel = [...nav]
    .sort((a, b) => b.to.length - a.to.length)
    .find(
      (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
    )?.label;
  // Longest prefix here too. Several tab groups lead with the section root
  // (/learn, /practice), which prefixes every sibling — matched in declaration
  // order, /learn/basics resolves to the "Subjects" tab and the breadcrumb
  // freezes on the first tab for the whole section.
  const tabLabel = [...(tabs ?? [])]
    .sort((a, b) => b.to.length - a.to.length)
    .find((t) => location.pathname === t.to || location.pathname.startsWith(`${t.to}/`))?.label;

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  function toggleSidebar() {
    setSidebarCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem(collapseKey, String(next));
      return next;
    });
  }

  const footerLink = (collapsed: boolean) =>
    footerNav && (
      <NavLink
        to={footerNav.to}
        aria-label={collapsed ? footerNav.label : undefined}
        title={collapsed ? footerNav.label : undefined}
        style={({ isActive }) => sidebarLinkStyle(isActive, collapsed)}
      >
        <Icon name={footerNav.icon} size={17} />
        <span className={collapsed ? 'sr-only' : undefined}>{footerNav.label}</span>
      </NavLink>
    );

  return (
    <div className="min-h-screen bg-paper text-ink">
      <aside
        data-surface="inverse"
        className={`fixed inset-y-0 left-0 z-50 hidden flex-col gap-5 p-4 lg:flex ${
          sidebarCollapsed ? 'w-[72px]' : 'w-52'
        }`}
        style={{
          background: 'var(--surface-inverse)',
          color: 'var(--sidebar-fg)',
          transition: 'width var(--dur-base) var(--ease-standard)',
        }}
      >
        <NavLink
          to={homeTo}
          aria-label={sidebarCollapsed ? 'CWY home' : undefined}
          className={`flex items-center text-white no-underline ${
            sidebarCollapsed ? 'justify-center' : 'gap-2.5 px-1'
          }`}
        >
          <img
            src="/cwy-mark.svg"
            alt="CWY"
            width={30}
            height={30}
            style={{ borderRadius: '7px' }}
          />
          {!sidebarCollapsed && <span className="text-fs-16 font-extrabold">CWY</span>}
        </NavLink>
        <PrimaryNav items={nav} collapsed={sidebarCollapsed} />
        {/* The account lives in the header, not here — see the header's own
            note. All that is pinned to the bottom of the sidebar is the
            role's one footer destination, if it has one. */}
        <div className="mt-auto flex flex-col gap-3">{footerLink(sidebarCollapsed)}</div>
      </aside>

      <div
        className={`flex min-h-screen flex-col ${sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-52'}`}
        style={{ transition: 'padding var(--dur-base) var(--ease-standard)' }}
      >
        {/* The header belongs to the content area, not to the sidebar.
            Both were forest, so the two read as one undifferentiated slab and
            the eye had nothing to separate navigation from the page. Paper with
            a hairline under it gives the sidebar an edge to be an edge against. */}
        <header
          className={`sticky top-0 z-40 flex items-center gap-3 px-4 py-2.5 ${
            hasTabs ? '' : 'border-b border-line'
          }`}
          style={{ background: 'var(--surface-card)', color: 'var(--text-body)' }}
        >
          <div className="flex items-center gap-3 lg:hidden">
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              className="flex items-center justify-center rounded-lg p-1.5 text-ink-soft transition hover:bg-paper-sunk hover:text-forest"
              style={{ border: 'none', background: 'transparent' }}
            >
              <Icon name="menu" size={22} />
            </button>
            <NavLink to={homeTo} className="flex items-center gap-2 text-forest no-underline">
              <img
                src="/cwy-mark.svg"
                alt="CWY"
                width={28}
                height={28}
                style={{ borderRadius: '7px' }}
              />
              <span className="text-fs-15 font-extrabold">CWY</span>
            </NavLink>
          </div>

          {/* Desktop collapse control. In the header rather than floating on the
              sidebar's edge: it is a property of the layout, so it sits with the
              other layout chrome and never overlaps the nav it controls. */}
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden items-center justify-center rounded-lg p-1.5 text-ink-soft hover:bg-paper-sunk hover:text-forest lg:flex"
            style={{
              border: 'none',
              background: 'transparent',
              transition:
                'background var(--dur-fast) var(--ease-standard), color var(--dur-fast) var(--ease-standard)',
            }}
          >
            <Icon name="panel-left" size={18} />
          </button>

          {/* Section › tab. This is the only place the current screen is named
              once the sidebar is collapsed, and it is what the page H1 sits
              under — so pages title the specific screen, never their own
              section. */}
          {sectionLabel && (
            <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2">
              <span aria-hidden className="hidden text-line-strong lg:inline">
                |
              </span>
              <span
                className={`truncate text-sm ${
                  tabLabel ? 'text-ink-soft' : 'font-semibold text-ink'
                }`}
              >
                {sectionLabel}
              </span>
              {tabLabel && (
                <>
                  <span aria-hidden className="text-ink-faint">
                    ›
                  </span>
                  <span className="truncate text-sm font-semibold text-ink">{tabLabel}</span>
                </>
              )}
            </nav>
          )}

          <div className="ml-auto flex items-center gap-2.5">
            {subtitle && (
              <span className="hidden text-xs font-semibold text-ink-soft sm:inline">
                {subtitle}
              </span>
            )}
            {/* Always visible, never behind a click: which authority you are
                acting with is the thing that should not be a surprise, and a
                school admin who has forgotten they are an admin is exactly the
                mistake this prevents. */}
            <RoleBadge />
            {headerExtras}
            {/* After the bell, and in the shell rather than in each role's
                headerExtras, so staff get the theme control too — the streak
                pill and the bell are the student-only parts. */}
            <ThemeToggle />
            {/* One place, always. It used to sit at the foot of the sidebar and
                move up here only once the sidebar was collapsed, so "where do I
                sign out" had two answers depending on a layout preference — and
                on mobile, an answer behind the drawer. */}
            <ProfileMenu />
          </div>
        </header>

        <AnimatePresence>
          {drawerOpen && (
            <>
              <motion.div
                key="drawer-backdrop"
                className="fixed inset-0 z-40 lg:hidden"
                style={{ background: 'rgba(0,0,0,.45)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                onClick={() => setDrawerOpen(false)}
              />
              <motion.aside
                key="drawer-panel"
                data-surface="inverse"
                className="fixed left-0 top-0 z-50 flex h-full w-[232px] flex-none flex-col gap-4 p-4 lg:hidden"
                style={{ background: 'var(--surface-inverse)', color: 'var(--sidebar-fg)' }}
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: 'tween', duration: 0.22, ease: 'easeOut' }}
              >
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2.5">
                    <img
                      src="/cwy-mark.svg"
                      alt="CWY"
                      width={28}
                      height={28}
                      style={{ borderRadius: '7px' }}
                    />
                    <span className="text-fs-15 font-extrabold">CWY</span>
                  </div>
                  <button
                    onClick={() => setDrawerOpen(false)}
                    aria-label="Close menu"
                    className="rounded-lg p-1 transition hover:bg-white/10"
                    style={{
                      color: 'var(--sidebar-fg)',
                      border: 'none',
                      background: 'transparent',
                    }}
                  >
                    <Icon name="x" size={18} />
                  </button>
                </div>
                <PrimaryNav items={nav} />
                <div className="mt-auto flex flex-col gap-3">{footerLink(false)}</div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <SectionTabs tabs={tabs} label="Section" />

        {/* One content column for both roles. Student pages constrain
            themselves with StudentPage (max-w-5xl) while staff pages relied on
            the old top-bar shell's 1000px main, so the two roles read at
            different widths on the same screen size. */}
        <main className="mx-auto w-full max-w-5xl flex-1 px-5 py-6">
          {/* Keyed on pathname so each screen fades and lifts in rather than
              appearing instantly. `mode="wait"` would blank the column for the
              exit duration on every click, which reads as slower than no
              animation at all — so the incoming screen simply replaces the old
              one. No AnimatePresence: an exiting copy would render <Outlet /> for the
              new route too, duplicating the page. Distance is small on purpose; the point is continuity, not
              travel. */}
          <motion.div
            key={location.pathname}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
